'use client'

import { Button } from '@/ui/shadcn/button';
import { Loader2, X } from 'lucide-react';
import React, { FormEvent, useState } from 'react';
import { Label } from '@/ui/shadcn/label';
import { Input } from '@/ui/shadcn/input';
import Link from 'next/link';
import { useModal } from '../ModalContext';
import LoginWhatsappModal from './LoginWhatsapp';
import { signIn } from '@/lib/auth-client';
import { redirect } from 'next/navigation';

interface LoginPopupData {
	title?: string;
	subtitle?: string;
	linkLabel?: string;
	linkUrl?: string;
}

const DEFAULT_LOGIN_DATA: LoginPopupData = {
	title: 'Bienvenue chez Dundshop !',
	subtitle: 'Inscrivez-vous ou connectez-vous pour continuer',
	linkLabel: 'Aperçu de notre Politique de Confidentialité',
	linkUrl: '#',
}

const LoginModal = ({ closeAction }: { closeAction: () => void }) => {

    const [isPending, setIsPending] = useState(false)
	const [error, setError] = useState<string | null>(null)
    const { openModal, closeModal, loginPopupData } = useModal()

	const loginData = loginPopupData || DEFAULT_LOGIN_DATA

    async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault()
		setIsPending(true)
		setError(null)

		const formData = new FormData(event.currentTarget)
		const email = formData.get('email') as string
		const password = formData.get('password') as string

		await signIn.email(
            {
                email,
                password,
            },
            {
                onResponse: (ctx) => {
                    setIsPending(false);
                },
                onSuccess: (ctx) => {
                    closeAction();
                    window.location.href = "/account/profile";
                },
                onError: (ctx) => {
                    if (ctx.error.status === 403) {
                        setError("Please verify your email address");
                    } else {
                        setError(ctx.error.message);
                    }
                },
            },
        );
	}

    const signInSocialAction = async (provider: string) => {
        // Use current pathname as callback URL for better UX
        const callbackURL = window.location.pathname || "/"
        
        await signIn.social(
            {
                provider: provider,
                callbackURL
            },
            {
                onRequest: (ctx) => {
                    setIsPending(true);
                },
                onResponse: (ctx) => {
                    setIsPending(false);
                },
            },
        );
    }

    return (
        <div className="login-modal flex flex-col items-center">
            <button className="absolute top-6 right-6 cursor-pointer" onClick={closeAction}>
                <X className="size-6" />
            </button>
            <h2 className="text-xl md:text-3xl font-alt text-center md:text-left font-regular mb-3 md:mb-4 text-dark">
                {loginData.title}
            </h2>
            <p className="text-sm md:text-base w-full mb-4 text-center">
                {loginData.subtitle}
            </p>
            <div className="hidden md:flex flex-col gap-3 md:gap-5 w-full">
                <Button 
                    variant="popinLogin" size="popinLogin" className="w-full"
                    onClick={() => {
                        closeAction()
                        openModal(<LoginWhatsappModal closeAction={closeModal} />)
                    }}
                >
                    <img src="https://dunshop-s3.s3.eu-north-1.amazonaws.com/DundShop/Logo_Message.svg" alt="Message" className="w-6 md:w-9 h-6 md:h-9 mr-1" />
                    Se connecter par SMS
                </Button>
                <Button variant="popinLogin" size="popinLogin" className="w-full" onClick={() => signInSocialAction("facebook")}>
                    <img src="https://dunshop-s3.s3.eu-north-1.amazonaws.com/DundShop/Logo_Facebook.svg" alt="Facebook" className="w-6 md:w-9 h-6 md:h-9 mr-1" />
                    Se connecter avec Facebook
                </Button>
                <Button variant="popinLogin" size="popinLogin" className="w-full" onClick={() => signInSocialAction("google")}>
                    <img src="https://dunshop-s3.s3.eu-north-1.amazonaws.com/DundShop/Logo_Google.svg" alt="Google" className="w-6 md:w-9 h-6 md:h-9 mr-1" />
                    Se connecter avec Google
                </Button>
            </div>
            <div className="grid grid-cols-3 md:hidden w-full gap-3">
                <div 
                    className="flex flex-col items-center justify-center gap-3 border border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-100 transition-all duration-300"
                    onClick={() => {
                        closeAction()
                        openModal(<LoginWhatsappModal closeAction={closeModal} />)
                    }}
                >
                    <img src={`https://dunshop-s3.s3.eu-north-1.amazonaws.com/DundShop/Logo_Message.svg`} alt={`Message`} className="w-6 md:w-9 h-6 md:h-9" />
                    <span className="text-sm font-medium">SMS</span>
                </div>
                <div 
                    className="flex flex-col items-center justify-center gap-3 border border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-100 transition-all duration-300"
                    onClick={() => signInSocialAction("facebook")}
                >
                    <img src={`https://dunshop-s3.s3.eu-north-1.amazonaws.com/DundShop/Logo_Facebook.svg`} alt={`Facebook`} className="w-6 md:w-9 h-6 md:h-9" />
                    <span className="text-sm font-medium">Facebook</span>
                </div>
                <div 
                    className="flex flex-col items-center justify-center gap-3 border border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-100 transition-all duration-300"
                    onClick={() => signInSocialAction("google")}
                >
                    <img src={`https://dunshop-s3.s3.eu-north-1.amazonaws.com/DundShop/Logo_Google.svg`} alt={`Google`} className="w-6 md:w-9 h-6 md:h-9" />
                    <span className="text-sm font-medium">Google</span>
                </div>
            </div>
            <div className="my-4 md:my-6 px-4 flex items-center">
                <hr className="w-[130px] h-[1px] border-black" />
                <p className="text-center mx-4 text-sm font-medium text-dark">ou</p>
                <hr className="w-[130px] h-[1px] border-black" />
            </div>
            <form className="w-full" onSubmit={onSubmit}>
                <div className="w-full space-y-2 mb-2">
                    <Label htmlFor="email">Votre adresse e-mail :</Label>
                    <Input id="email" name="email" type="text" placeholder="Exemple : email@email.com" />
                </div>
                <div className="w-full space-y-2 mb-5">
                    <Label htmlFor="password">Votre mot de passe :</Label>
                    <Input id="password" name="password" type="password" />
                </div>
                <Button
                    type="submit" size="xl"
                    className="w-full mb-3"
                    disabled={isPending}
                >
                    {isPending && (
                        <Loader2 className="animate-spin" />
                    )}
                    Se connecter
                </Button>
            </form>
            {error && (
                <div className="w-full mb-4 text-red-600 text-center">
                    {error}
                </div>
            )}
            <Link href="/account/create" onClick={() => closeAction()} className="w-full mb-3">
                <Button size="xl" variant="outline" className="w-full">
                    Créer un compte
                </Button>
            </Link>
            <Link 
                href={loginData.linkUrl} 
                className="text-xs color-dark underline block text-center mt-4"
            >
                {loginData.linkLabel}
            </Link>
        </div>
    );
};

export default LoginModal;
